import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Image } from '../Image/Image';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { buildLanguageUrl } from '../../utils/languageUrl';
import { TopBar } from './TopBar';
import { FooterColumns } from './FooterColumns';
import { BottomBar } from './BottomBar';
import { PopupDialog } from './PopupDialog';
import { PageFooterNestedProps, ActionItem } from './types';
import './PageFooter.scss';

// Built-in logo presets
const LOGO_PRESETS: Record<string, { dark: string; light: string }> = {
  amazon: {
    dark: 'https://m.media-amazon.com/images/G/01/sell/navigation/logos/amazon-logo_rgb_wht.svg',
    light: 'https://m.media-amazon.com/images/G/01/sell/navigation/amazon-logo-squid-menu.svg',
  },
};

/** Resolve logo URL from preset name or custom URL, respecting theme */
function resolveLogoUrl(logoImageUrl: string | undefined, theme: 'dark' | 'light'): string | undefined {
  if (!logoImageUrl) return undefined;
  const preset = LOGO_PRESETS[logoImageUrl];
  if (preset) return theme === 'light' ? preset.light : preset.dark;
  return logoImageUrl;
}

/** Renders the icon for an action button — sprite, image, or nothing. */
function renderActionIcon(action: ActionItem): React.ReactNode {
  // Language picker (slot 1): use the shared MegaMenu language icon for visual parity.
  if (action.slot === 1) {
    return (
      <CustomIcon
        name="language"
        size="var(--poly-size-megaMenu-iconSize)"
        aria-hidden="true"
      />
    );
  }
  if (action.spriteY != null && action.iconUrl) {
    return (
      <span
        className="poly-page-footer__action-icon poly-page-footer__action-icon--sprite"
        aria-hidden="true"
        style={{ backgroundImage: `url(${action.iconUrl})`, backgroundPosition: `-3px -${action.spriteY}px` }}
      />
    );
  }
  if (action.iconUrl) {
    return <Image bare url={action.iconUrl} alt="" className="poly-page-footer__locale-icon" aria-hidden="true" />;
  }
  return null;
}

export const PageFooterNested: React.FC<PageFooterNestedProps> = (props) => {
  const {
    theme = 'dark',
    topBarText,
    topBarLinkText,
    topBarLinkUrl,
    topBarCtaId,
    logoImageUrl,
    logoAlt,
    logoUrl,
    logoCtaId,
    logoLdAppendix,
    copyrightText,
    disclaimerText,
    footnotesText,
    row1AriaLabel,
    row2AriaLabel,
    siteMapAriaLabel,
    socialAriaLabel,
    breadcrumbAriaLabel,
    currentLanguage,
    languagePickerHeading = 'Select your preferred language',
    languagePickerAddPathLocale = false,
    currentCountry,
    countryPickerHeading = 'Explore selling on Amazon around the world',
    countryPickerFootnote,
    countrySpriteUrl,
    row1Links = [],
    row2Links = [],
    socialLinks = [],
    columns = [],
    actions = [],
    breadcrumbs = [],
    languages = [],
    countries = [],
    className,
    ...rest
  } = props;

  const [openPopup, setOpenPopup] = useState<'language' | 'country' | null>(null);
  const langTriggerRef = useRef<HTMLButtonElement>(null);
  const countryTriggerRef = useRef<HTMLButtonElement>(null);

  const closePopup = useCallback(() => {
    const trigger = openPopup === 'language' ? langTriggerRef : countryTriggerRef;
    setOpenPopup(null);
    trigger.current?.focus();
  }, [openPopup]);

  const togglePopup = useCallback((type: 'language' | 'country') => {
    setOpenPopup(prev => prev === type ? null : type);
  }, []);

  // Capture URL state client-side after mount to keep server and client
  // renders consistent (avoids hydration mismatch on language picker hrefs).
  const [clientUrl, setClientUrl] = useState<{ pathname: string; search: string }>({
    pathname: '',
    search: '',
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setClientUrl({
      pathname: window.location.pathname,
      search: window.location.search,
    });
  }, []);

  const buildLangUrl = (langCode: string) =>
    buildLanguageUrl(
      langCode,
      currentLanguage ?? '',
      languagePickerAddPathLocale,
      clientUrl.pathname,
      clientUrl.search
    );

  /** Resolve action button behavior based on slot-to-popup mapping. */
  function getActionBehavior(action: ActionItem) {
    if (action.slot === 1 && languages.length > 0) {
      return { ref: langTriggerRef, onClick: () => togglePopup('language'), popupType: 'language' as const };
    }
    if (action.slot === 2 && countries.length > 0) {
      return { ref: countryTriggerRef, onClick: () => togglePopup('country'), popupType: 'country' as const };
    }
    return { ref: undefined, onClick: action.onClick, popupType: undefined };
  }

  return (
    <footer
      // role="contentinfo" is intentional even though <footer> implies it:
      // the implicit role only applies when <footer> is a direct child of
      // <body> or a top-level landmark. PolyR injects this component inside
      // a wrapper <div>, which downgrades the implicit role to "generic".
      // Setting the role explicitly preserves the landmark for screen readers
      // regardless of host page structure.
      role="contentinfo"
      className={`poly-page-footer poly-page-footer--${theme}${theme === 'dark' ? ' awsui-polaris-dark-mode' : ''} ${className || ''}`.trim()}
      {...rest}
    >
      {/* Top bar CTA */}
      {topBarText && (
        <TopBar text={topBarText} linkText={topBarLinkText} linkUrl={topBarLinkUrl} ctaId={topBarCtaId} />
      )}

      {/* Footnotes + Disclaimer */}
      {(footnotesText || disclaimerText) && (
        <div className="poly-page-footer__divider"><hr /></div>
      )}
      {(footnotesText || disclaimerText) && (
        <div className="poly-page-footer__meta-row">
          {footnotesText && (
            <div className="poly-page-footer__footnotes">
              <ReactMarkdown>{footnotesText}</ReactMarkdown>
            </div>
          )}
          {disclaimerText && (
            <div className="poly-page-footer__disclaimer">
              <ReactMarkdown>{disclaimerText}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {(footnotesText || disclaimerText) && (
        <div className="poly-page-footer__divider"><hr /></div>
      )}

      {/* Breadcrumb row + locale action buttons */}
      {(breadcrumbs.length > 0 || actions.length > 0) && (
        <div className="poly-page-footer__breadcrumb-row">
          {breadcrumbs.length > 0 && (
            <nav aria-label={breadcrumbAriaLabel || 'Breadcrumb'} className="poly-page-footer__breadcrumbs">
              <ol>
                {breadcrumbs.map((item, i) => {
                  const isLast = i === breadcrumbs.length - 1;
                  return (
                    <li key={i}>
                      {item.url
                        ? <a href={item.url} {...(isLast ? { 'aria-current': 'page' as const } : {})}>{item.text}</a>
                        : <span {...(isLast ? { 'aria-current': 'page' as const } : {})}>{item.text}</span>
                      }
                      {!isLast && (
                        <CustomIcon
                          name="chevron-right"
                          className="poly-page-footer__breadcrumb-separator"
                          aria-hidden="true"
                        />
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          )}
          {actions.length > 0 && (
            <div className="poly-page-footer__locale-links">
              {actions.map((action, i) => {
                const { ref, onClick, popupType } = getActionBehavior(action);
                return (
                  <button
                    key={i}
                    type="button"
                    className="poly-page-footer__locale-btn"
                    ref={ref}
                    onClick={onClick}
                    {...(popupType ? { 'aria-expanded': openPopup === popupType, 'aria-haspopup': 'dialog' as const } : {})}
                  >
                    {renderActionIcon(action)}
                    <span>{action.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Link Columns */}
      <FooterColumns columns={columns} ariaLabel={siteMapAriaLabel} />

      {columns.length > 0 && (
        <div className="poly-page-footer__divider"><hr /></div>
      )}

      {/* Bottom bar */}
      <BottomBar
        logoImageUrl={resolveLogoUrl(logoImageUrl, theme)}
        logoAlt={logoAlt}
        logoUrl={logoUrl}
        logoCtaId={logoCtaId}
        logoLdAppendix={logoLdAppendix}
        copyrightText={copyrightText}
        row1Links={row1Links}
        row2Links={row2Links}
        row1AriaLabel={row1AriaLabel}
        row2AriaLabel={row2AriaLabel}
        socialAriaLabel={socialAriaLabel}
        socialLinks={socialLinks}
      />

      {/* Language Picker Popup */}
      {openPopup === 'language' && (
        <PopupDialog ariaLabel={languagePickerHeading} onClose={closePopup} ignoreRefs={[langTriggerRef, countryTriggerRef]}>
          <h3 className="poly-page-footer__popup-heading">{languagePickerHeading}</h3>
          <ul className="poly-page-footer__lang-list" role="list">
            {languages.map((lang) => (
              <li key={lang.code}>
                <a
                  href={buildLangUrl(lang.code)}
                  className={`poly-page-footer__lang-link${lang.code === currentLanguage ? ' poly-page-footer__lang-link--current' : ''}`}
                  aria-current={lang.code === currentLanguage ? 'true' : undefined}
                >
                  {lang.name}
                </a>
              </li>
            ))}
          </ul>
        </PopupDialog>
      )}

      {/* Country Picker Popup */}
      {openPopup === 'country' && (
        <PopupDialog ariaLabel={countryPickerHeading} onClose={closePopup} ignoreRefs={[langTriggerRef, countryTriggerRef]}>
          <h3 className="poly-page-footer__popup-heading">{countryPickerHeading}</h3>
          <ul className="poly-page-footer__country-list" role="list">
            {countries.map((country) => (
              <li key={country.code}>
                <a
                  href={country.url}
                  className={`poly-page-footer__country-link${country.code === currentCountry ? ' poly-page-footer__country-link--current' : ''}`}
                  aria-current={country.code === currentCountry ? 'true' : undefined}
                >
                  {countrySpriteUrl && country.spriteY != null && (
                    <span
                      className="poly-page-footer__country-flag"
                      role="img"
                      aria-hidden="true"
                      style={{ backgroundImage: `url(${countrySpriteUrl})`, backgroundPosition: `-3px -${country.spriteY}px` }}
                    />
                  )}
                  {country.name}
                </a>
              </li>
            ))}
          </ul>
          {countryPickerFootnote && <p className="poly-page-footer__country-footnote">{countryPickerFootnote}</p>}
        </PopupDialog>
      )}
    </footer>
  );
};
